import React, { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { exportElementToA4Pdf } from '../../utils/exportElementToPdf';
import { moduleConfig } from './config';
import { useMgaPayslip } from './useData';

/** Compact plain amount for B&W print (no currency symbol clutter). */
function formatAmt(n: number | null | undefined) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n));
}

function isNonZero(n: number | null | undefined) {
  return Number.isFinite(Number(n)) && Math.abs(Number(n)) > 0.0001;
}

const Payslip: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const { id, lineId } = useParams<{ id: string; lineId: string }>();
  const periodId = id ? parseInt(id, 10) : NaN;
  const lid = lineId ? parseInt(lineId, 10) : NaN;
  const { data, isLoading } = useMgaPayslip(
    Number.isInteger(periodId) ? periodId : 0,
    Number.isInteger(lid) ? lid : 0,
  );
  const [exporting, setExporting] = useState(false);
  const payslipRef = useRef<HTMLElement>(null);

  if (!Number.isInteger(periodId) || !Number.isInteger(lid)) {
    return (
      <div className="p-8 text-center">
        <Link to={moduleConfig.module.route.list} className="text-blue-600 underline">
          Back to MGA
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>Payslip not found</h1>
        <Link to={`/mga/${periodId}`} className="mt-4 inline-block text-sm text-blue-600 underline">
          Back to period
        </Link>
      </div>
    );
  }

  const { period, line } = data;
  const ccy = period.currency || 'USD';
  const otTotal =
    line.otTotal ?? (Number(line.overtimeHours) || 0) * (Number(line.overtimeRate) || 0);
  const earnings = (line.wageValues ?? []).filter(
    (v) => v.columnType !== 'DEDUCTION' && v.columnType !== 'INFO' && v.includeInTotal,
  );
  const wageDeds = (line.wageValues ?? []).filter(
    (v) => v.columnType === 'DEDUCTION' && v.includeInTotal && isNonZero(v.periodAmount),
  );

  const structuredDeds: Array<{ label: string; value: number }> = [
    { label: 'Cash advance (office)', value: Number(line.cashAdvanceOffice) || 0 },
    { label: 'Cash advance (master)', value: Number(line.cashAdvanceMaster) || 0 },
    { label: 'Allotments', value: Number(line.allotmentAmount) || 0 },
    { label: 'Cigarettes', value: Number(line.cigarettesAmount) || 0 },
    { label: 'Internet cards', value: Number(line.internetCardsAmount) || 0 },
    { label: 'Other bonded', value: Number(line.otherBondedAmount) || 0 },
    { label: 'Various', value: Number(line.variousDeductions) || 0 },
  ].filter((d) => isNonZero(d.value));

  const LineRow = ({
    label,
    value,
    emph,
    deduct,
  }: {
    label: string;
    value: React.ReactNode;
    emph?: boolean;
    deduct?: boolean;
  }) => (
    <tr className={cn(emph && 'mga-payslip-emph')}>
      <td className="mga-payslip-label">
        {deduct ? `− ${label}` : label}
      </td>
      <td className="mga-payslip-amt">{value}</td>
    </tr>
  );

  return (
    <div className={cn('min-h-full', colors.background)}>
      <div className="mx-auto max-w-[210mm] space-y-3 px-4 py-5 print:max-w-none print:space-y-0 print:p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
          <Link
            to={`/mga/${periodId}`}
            className={cn('text-sm font-medium', colors.textSecondary, 'hover:underline')}
          >
            ← Back to period
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={exporting}
              onClick={async () => {
                if (!payslipRef.current) return;
                setExporting(true);
                try {
                  const abrv = (
                    period.vessel?.vesselAbrv ||
                    period.vessel?.vesselName ||
                    'vessel'
                  )
                    .replace(/[^\w.-]+/g, '_')
                    .slice(0, 40);
                  const crewSlug = (line.crewName || `line_${line.id}`)
                    .replace(/[^\w.-]+/g, '_')
                    .slice(0, 40);
                  const from = String(period.periodFrom).slice(0, 10);
                  await exportElementToA4Pdf(
                    payslipRef.current,
                    `MGA_${abrv}_${from}_Payslip_${crewSlug}`,
                  );
                  showSuccess('Payslip PDF exported.');
                } catch (e: unknown) {
                  showError(e instanceof Error ? e.message : 'Failed to export PDF.');
                } finally {
                  setExporting(false);
                }
              }}
            >
              {exporting ? 'Exporting…' : 'Export PDF'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => window.print()}>
              Print
            </Button>
          </div>
        </div>

        <article ref={payslipRef} className="app-print-root mga-payslip">
          <header className="mga-payslip-header">
            <div className="mga-payslip-header-banner">
              <p className="mga-payslip-eyebrow">Master&apos;s general account</p>
              <h1>Monthly pay account</h1>
              <p className="mga-payslip-vessel">
                M.V. {period.vessel?.vesselName ?? `#${period.vesselId}`}
              </p>
            </div>
            <table className="mga-payslip-header-bar">
              <tbody>
                <tr>
                  <td>
                    <span className="mga-payslip-bar-label">Period</span>
                    <span className="mga-payslip-bar-value">
                      {formatDateGlobal(period.periodFrom)} – {formatDateGlobal(period.periodTo)}
                    </span>
                  </td>
                  <td>
                    <span className="mga-payslip-bar-label">Currency</span>
                    <span className="mga-payslip-bar-value">{ccy}</span>
                  </td>
                  <td>
                    <span className="mga-payslip-bar-label">Sheet</span>
                    <span className="mga-payslip-bar-value">No. {period.id}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </header>

          <table className="mga-payslip-id">
            <tbody>
              <tr>
                <th>Name</th>
                <td>{line.crewName}</td>
                <th>Rank</th>
                <td>{line.rankName || '—'}</td>
              </tr>
              <tr>
                <th>Sign on</th>
                <td>{line.signOnDate ? formatDateGlobal(line.signOnDate) : '—'}</td>
                <th>Days</th>
                <td>{line.daysInPeriod}</td>
              </tr>
            </tbody>
          </table>

          <div className="mga-payslip-stack">
            <section>
              <h2>Income</h2>
              <table className="mga-payslip-ledger">
                <tbody>
                  {earnings.map((v) => (
                    <LineRow
                      key={v.id}
                      label={v.columnName || v.columnCode}
                      value={formatAmt(v.periodAmount)}
                    />
                  ))}
                  {isNonZero(line.extraEarnings) && (
                    <LineRow label="Extra earnings" value={formatAmt(line.extraEarnings)} />
                  )}
                  {(isNonZero(otTotal) || isNonZero(line.overtimeHours)) && (
                    <LineRow
                      label={`Overtime (${line.overtimeHours || 0} h × ${formatAmt(line.overtimeRate)})`}
                      value={formatAmt(otTotal)}
                    />
                  )}
                  {isNonZero(line.previousBalance) && (
                    <LineRow label="Previous balance" value={formatAmt(line.previousBalance)} />
                  )}
                  <LineRow label="Total income" value={formatAmt(line.grossPayable)} emph />
                </tbody>
              </table>
            </section>

            <section>
              <h2>Deductions</h2>
              <table className="mga-payslip-ledger">
                <tbody>
                  {wageDeds.map((v) => (
                    <LineRow
                      key={v.id}
                      label={v.columnName || v.columnCode}
                      value={formatAmt(v.periodAmount)}
                      deduct
                    />
                  ))}
                  {structuredDeds.map((d) => (
                    <LineRow
                      key={d.label}
                      label={d.label}
                      value={formatAmt(d.value)}
                      deduct
                    />
                  ))}
                  {!wageDeds.length && !structuredDeds.length && (
                    <tr>
                      <td className="mga-payslip-label mga-payslip-muted" colSpan={2}>
                        None
                      </td>
                    </tr>
                  )}
                  <LineRow
                    label="Total deductions"
                    value={formatAmt(line.totalDeductions ?? line.deductions)}
                    emph
                    deduct
                  />
                </tbody>
              </table>
            </section>
          </div>

          <section className="mga-payslip-balance">
            {line.remarks ? (
              <div className="mga-payslip-remarks">Remarks: {line.remarks}</div>
            ) : null}
            <div className="mga-payslip-balance-main">
              <div className="mga-payslip-balance-label">Balance due to crew ({ccy})</div>
              <div className="mga-payslip-balance-value">{formatAmt(line.netDue)}</div>
            </div>
          </section>

          <footer className="mga-payslip-sign">
            <div>
              <div className="mga-payslip-sign-line" />
              <div className="mga-payslip-sign-title">Receiver / Crew</div>
              <div className="mga-payslip-muted">Name &amp; signature</div>
            </div>
            <div>
              <div className="mga-payslip-sign-line" />
              <div className="mga-payslip-sign-title">Master</div>
              <div className="mga-payslip-muted">Name &amp; signature</div>
            </div>
          </footer>
        </article>
      </div>
    </div>
  );
};

export default Payslip;
